import type { HeroPricing } from "@scrile/contracts";
import { intlLocale, t } from "../i18n";

/**
 * Цены пейвола. Деньги приходят целыми центами (контракт); в доллары они превращаются
 * только здесь и только для показа — никакой арифметики на дробях.
 */

export type PlanId = "yearly" | "monthly" | "all_at_once";

const formatters = new Map<string, Intl.NumberFormat>();

/** 5999 → "$59.99". Копейки показываются всегда: «$5.00/mo» честнее, чем «$5/mo» рядом с «$59.99». */
export function formatCents(cents: number, currency: string = "usd", locale: string = intlLocale()): string {
  const key = `${locale}:${currency}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    formatters.set(key, formatter);
  }
  return formatter.format(cents / 100);
}

/**
 * Годовой план в пересчёте на месяц, в центах: 5999 → 500 ("$5.00").
 * Округление обычное, а не вниз: $4.99 при фактических $4.9992 было бы занижением.
 */
export function monthlyEquivalentCents(yearlyCents: number): number {
  return Math.round(yearlyCents / 12);
}

/**
 * Экономия годового плана против двенадцати месяцев, целые проценты вниз:
 * обещать «сэкономите 38%», когда выходит 37.4, нельзя.
 */
export function yearlySavingsPercent(monthlyCents: number, yearlyCents: number): number {
  const fullYear = monthlyCents * 12;
  if (fullYear <= 0 || yearlyCents >= fullYear) return 0;
  return Math.floor(((fullYear - yearlyCents) / fullYear) * 100);
}

export interface PlanOffer {
  plan: PlanId;
  /** Крупная цена: "$5.00/mo" или "$4.99". */
  headline: string;
  /** Мелкая строка под ней: "billed $59.99/yr". */
  detail: string;
  /** "Save 37%" — только у годового и только если экономия есть. */
  badge: string | null;
  /** Сколько спишут при оформлении (после пробного периода для подписок). */
  chargeCents: number;
  /** Пробный период применяется: у подписок — да, у разовой покупки — нет. */
  trial: boolean;
  /** Стендовая симуляция: никакого реального списания, доступ истекает через сутки. */
  demoDayPass: boolean;
}

/**
 * Сколько дней пробного периода можно обещать. Уже использовавшему пробный период у
 * этого героя (Me.trialEligible = false) — ноль: Stripe спишет сразу, и «7 days free»
 * было бы обманом. Гость — будущий новый аккаунт, ему обещаем.
 */
export function trialDaysFor(pricing: HeroPricing, trialEligible: boolean | null): number {
  return trialEligible === false ? 0 : pricing.trialDays;
}

/**
 * Варианты пейвола по порядку показа: годовой первым (он же выбран по умолчанию),
 * месячный, «всё сразу» — если креатор его включил. trialEligible — из /me; null — гость.
 */
export function planOffers(pricing: HeroPricing, trialEligible: boolean | null = null): PlanOffer[] {
  const { currency } = pricing;
  const hasTrial = trialDaysFor(pricing, trialEligible) > 0;
  const savings = yearlySavingsPercent(pricing.monthlyCents, pricing.yearlyCents);
  const offers: PlanOffer[] = [
    {
      plan: "yearly",
      headline: perMonthLabel(monthlyEquivalentCents(pricing.yearlyCents), currency),
      detail: t("paywall.billedYearly", { price: formatCents(pricing.yearlyCents, currency) }),
      badge: savings > 0 ? t("paywall.save", { percent: savings }) : null,
      chargeCents: pricing.yearlyCents,
      trial: hasTrial,
      demoDayPass: false,
    },
    {
      plan: "monthly",
      headline: perMonthLabel(pricing.monthlyCents, currency),
      detail: t("paywall.billedMonthly"),
      badge: null,
      chargeCents: pricing.monthlyCents,
      trial: hasTrial,
      demoDayPass: false,
    },
  ];
  if (pricing.demoDayPass) {
    offers.push({
      plan: "all_at_once",
      headline: formatCents(pricing.demoDayPass.priceCents, currency),
      detail: t("paywall.demoDay.oneTime"),
      badge: t("paywall.demoDay.badge"),
      chargeCents: pricing.demoDayPass.priceCents,
      trial: false,
      demoDayPass: true,
    });
  } else if (pricing.allAtOnceCents !== null) {
    offers.push({
      plan: "all_at_once",
      headline: formatCents(pricing.allAtOnceCents, currency),
      detail: t("paywall.oneTime"),
      badge: null,
      chargeCents: pricing.allAtOnceCents,
      trial: false,
      demoDayPass: false,
    });
  }
  return offers;
}

/** "$5.00/mo" */
export function perMonthLabel(cents: number, currency: string = "usd"): string {
  return t("paywall.perMonth", { price: formatCents(cents, currency) });
}

/**
 * Строка под кнопкой оплаты: что будет после пробного периода. Человек должен
 * видеть сумму списания до того, как попадёт в Stripe.
 */
export function chargeLine(offer: PlanOffer, pricing: HeroPricing): string {
  const price = formatCents(offer.chargeCents, pricing.currency);
  if (offer.demoDayPass) return t("paywall.demoDay.charge", { price });
  if (offer.plan === "all_at_once") return t("paywall.charge.all_at_once", { price });
  if (offer.trial) {
    return offer.plan === "yearly"
      ? t("paywall.then.yearly", { days: pricing.trialDays, price })
      : t("paywall.then.monthly", { days: pricing.trialDays, price });
  }
  return offer.plan === "yearly" ? t("paywall.charge.yearly", { price }) : t("paywall.charge.monthly", { price });
}

/** Текст кнопки оплаты для выбранного варианта. */
export function ctaLabel(offer: PlanOffer, pricing: HeroPricing): string {
  if (offer.demoDayPass) {
    return t("paywall.demoDay.cta", { price: formatCents(offer.chargeCents, pricing.currency) });
  }
  if (offer.plan === "all_at_once") {
    return t("paywall.cta.all_at_once", { price: formatCents(offer.chargeCents, pricing.currency) });
  }
  return offer.trial ? t("paywall.cta.trial", { days: pricing.trialDays }) : t("paywall.cta.subscribe");
}
